import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const MENU_HISTORY_KEY = '__simedgeMobileMenu';
const MAIN_DRAWER_CLOSE_MS = 280;
const MAIN_DRAWER_CLOSE_FALLBACK_MS = MAIN_DRAWER_CLOSE_MS + 120;
const SWIPE_START_DISTANCE = 10;
const SWIPE_CLOSE_DISTANCE = 96;
const SWIPE_CLOSE_MIN_DISTANCE = 42;
const SWIPE_CLOSE_VELOCITY = 0.5;
const SWIPE_RESET_MS = 180;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 * @property {HTMLElement} [scrollIndicator] - Indicates when the active menu panel has more content below.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement | null} */
  #lastFocusedElement = null;

  /** @type {'closed' | 'opening' | 'open' | 'closing'} */
  #menuState = 'closed';

  #transitionVersion = 0;

  /** @type {Promise<unknown>} */
  #closePromise = Promise.resolve();

  #historyEntryActive = false;

  #historyToken = `simedge-mobile-menu-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  /** @type {(() => void) | null} */
  #historyCleanupResolve = null;

  /** @type {MutationObserver | null} */
  #contentObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #scrollIndicatorFrame = 0;

  /** @type {{ startX: number; startY: number; lastY: number; startTime: number; lastTime: number; active: boolean; cancelled: boolean } | null} */
  #swipeGesture = null;

  /** @type {Animation | null} */
  #drawerAnimation = null;

  /** @type {Animation | null} */
  #swipeResetAnimation = null;

  connectedCallback() {
    super.connectedCallback();

    this.#menuState = this.refs.details.hasAttribute('open') ? 'open' : 'closed';
    this.addEventListener('keyup', this.#onKeyUp);
    if (this.refs.menuDrawer.classList.contains('simedge-mobile-menu')) {
      this.refs.menuDrawer.addEventListener('touchstart', this.#onSwipeStart, { passive: true, capture: true });
      this.refs.menuDrawer.addEventListener('touchmove', this.#onSwipeMove, { passive: false, capture: true });
      this.refs.menuDrawer.addEventListener('touchend', this.#onSwipeEnd, { passive: true, capture: true });
      this.refs.menuDrawer.addEventListener('touchcancel', this.#onSwipeCancel, { passive: true, capture: true });
    }
    if (this.refs.scrollIndicator) {
      window.addEventListener('popstate', this.#onPopState);
      window.addEventListener('resize', this.#scheduleScrollIndicatorUpdate);
      this.querySelectorAll('[data-menu-panel]').forEach((panel) => {
        panel.addEventListener('scroll', this.#scheduleScrollIndicatorUpdate, { passive: true });
      });

      this.#contentObserver = new MutationObserver(this.#scheduleScrollIndicatorUpdate);
      this.#contentObserver.observe(this.refs.menuDrawer, { childList: true, subtree: true });

      if ('ResizeObserver' in window) {
        this.#resizeObserver = new ResizeObserver(this.#scheduleScrollIndicatorUpdate);
        this.#resizeObserver.observe(this.refs.menuDrawer);
        this.querySelectorAll('[data-menu-panel]').forEach((panel) => this.#resizeObserver?.observe(panel));
      }
    }

    this.#setupAnimatedElementListeners();
    this.#scheduleScrollIndicatorUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    if (this.refs.menuDrawer.classList.contains('simedge-mobile-menu')) {
      this.refs.menuDrawer.removeEventListener('touchstart', this.#onSwipeStart, true);
      this.refs.menuDrawer.removeEventListener('touchmove', this.#onSwipeMove, true);
      this.refs.menuDrawer.removeEventListener('touchend', this.#onSwipeEnd, true);
      this.refs.menuDrawer.removeEventListener('touchcancel', this.#onSwipeCancel, true);
    }
    if (this.refs.scrollIndicator) {
      window.removeEventListener('popstate', this.#onPopState);
      window.removeEventListener('resize', this.#scheduleScrollIndicatorUpdate);
      this.querySelectorAll('[data-menu-panel]').forEach((panel) => {
        panel.removeEventListener('scroll', this.#scheduleScrollIndicatorUpdate);
      });
    }
    this.#contentObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    if (this.#scrollIndicatorFrame) cancelAnimationFrame(this.#scrollIndicatorFrame);
    this.#drawerAnimation?.cancel();
    this.#swipeResetAnimation?.cancel();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  #onPopState = () => {
    if (this.#historyCleanupResolve) {
      this.#historyCleanupResolve();
      this.#historyCleanupResolve = null;
      return;
    }

    if (!this.#historyEntryActive) return;

    this.#historyEntryActive = false;
    this.#close(this.refs.details, { skipHistoryCleanup: true });
  };

  #onSwipeStart = (event) => {
    const details = this.refs.details;
    if (
      this.#menuState === 'closing' ||
      !details.hasAttribute('open') ||
      !details.classList.contains('menu-open') ||
      event.touches.length !== 1
    ) {
      return;
    }

    const activePanel = this.querySelector('[data-menu-panel]:not([hidden])');
    if (!(activePanel instanceof HTMLElement) || activePanel.scrollTop > 1) return;

    const originalTarget = event.composedPath?.()[0] ?? event.target;
    if (
      originalTarget instanceof Element &&
      originalTarget.closest('input, select, textarea, [contenteditable="true"]')
    ) {
      return;
    }

    const touch = event.touches[0];
    this.#swipeGesture = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastY: touch.clientY,
      startTime: event.timeStamp,
      lastTime: event.timeStamp,
      active: false,
      cancelled: false,
    };
  };

  #onSwipeMove = (event) => {
    const gesture = this.#swipeGesture;
    if (!gesture || gesture.cancelled || this.#menuState === 'closing' || !this.refs.details.hasAttribute('open')) return;
    if (event.touches.length !== 1) {
      this.#cancelSwipeGesture();
      return;
    }

    const touch = event.touches[0];
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    const activePanel = this.querySelector('[data-menu-panel]:not([hidden])');

    gesture.lastY = touch.clientY;
    gesture.lastTime = event.timeStamp;

    if (!(activePanel instanceof HTMLElement) || activePanel.scrollTop > 1 || deltaY < 0) {
      this.#cancelSwipeGesture();
      return;
    }

    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= SWIPE_START_DISTANCE && Math.abs(deltaX) >= Math.abs(deltaY)) {
      this.#cancelSwipeGesture();
      return;
    }

    if (!gesture.active) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < SWIPE_START_DISTANCE) return;
      gesture.active = true;
      this.refs.details.classList.add('menu-swiping');
    }

    if (event.cancelable) event.preventDefault();
    const swipeDistance = Math.min(Math.max(deltaY, 0), window.innerHeight);
    this.refs.details.style.setProperty('--simedge-mobile-menu-swipe-y', `${swipeDistance}px`);
    this.refs.menuDrawer.style.transform = `translate3d(0, ${swipeDistance}px, 0)`;
  };

  #onSwipeEnd = (event) => {
    const gesture = this.#swipeGesture;
    this.#swipeGesture = null;
    if (!gesture?.active || gesture.cancelled) return;

    const endTouch = event.changedTouches?.[0];
    const endY = endTouch?.clientY ?? gesture.lastY;
    const endTime = event.timeStamp || gesture.lastTime;
    const distance = Math.max(endY - gesture.startY, 0);
    const duration = Math.max(endTime - gesture.startTime, 1);
    const velocity = distance / duration;

    if (distance >= SWIPE_CLOSE_DISTANCE || (distance >= SWIPE_CLOSE_MIN_DISTANCE && velocity >= SWIPE_CLOSE_VELOCITY)) {
      this.close();
      return;
    }

    this.#resetSwipePosition();
  };

  #onSwipeCancel = () => {
    this.#cancelSwipeGesture();
  };

  #cancelSwipeGesture() {
    const wasActive = this.#swipeGesture?.active;
    this.#swipeGesture = null;
    if (wasActive) this.#resetSwipePosition();
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.#menuState === 'opening' || this.#menuState === 'open';
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    const isMainDrawer = details === this.refs.details;

    if (!summary) return;

    if (isMainDrawer && this.#menuState === 'closing') return this.#closePromise;
    if (isMainDrawer && (this.#menuState === 'opening' || this.#menuState === 'open')) return;

    const transitionVersion = isMainDrawer ? ++this.#transitionVersion : 0;

    if (isMainDrawer) {
      this.#menuState = 'opening';
      this.#lastFocusedElement = summary;
      details.setAttribute('open', '');
      this.#pushHistoryEntry();
      syncScrollLock();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (isMainDrawer && transitionVersion !== this.#transitionVersion) return;

      details.classList.add('menu-open');

      if (details === this.refs.details) {
        this.#setActiveTab('products');
        this.#scheduleScrollIndicatorUpdate();
      }

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (isMainDrawer) {
            if (transitionVersion !== this.#transitionVersion || this.#menuState !== 'opening') return;
            this.#menuState = 'open';
            this.#scheduleScrollIndicatorUpdate();
          }

          trapFocus(details);
          if (details === this.refs.details && this.refs.productsTab instanceof HTMLElement) {
            this.refs.productsTab.focus();
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Select a mobile-menu tab.
   * @param {Event} event
   */
  selectTab(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const tab = event.target.closest('[role="tab"][data-menu-tab]');
    if (!(tab instanceof HTMLElement)) return;

    this.#setActiveTab(tab.dataset.menuTab, true);
  }

  /**
   * Close before following an ordinary navigation link so the temporary history state is removed.
   * @param {MouseEvent} event
   */
  handleNavigationClick = async (event) => {
    const anchor = event.composedPath().find((element) => element instanceof HTMLAnchorElement);
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (anchor.target && anchor.target !== '_self') return;
    if (anchor.hasAttribute('download')) return;

    event.preventDefault();
    await this.close();
    window.location.assign(anchor.href);
  };

  /**
   * Support the standard arrow, Home and End keys for the tab list.
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const tabs = Array.from(this.querySelectorAll('[role="tab"][data-menu-tab]')).filter(
      (tab) => tab instanceof HTMLElement
    );
    const currentIndex = tabs.indexOf(event.target);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    this.#setActiveTab(nextTab.dataset.menuTab, true);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    event?.preventDefault();
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    return this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   * @param {{ skipHistoryCleanup?: boolean }} [options]
   */
  #close(details, options = {}) {
    const summary = details.querySelector('summary');
    const isMainDrawer = details === this.refs.details;

    if (!summary) return Promise.resolve();

    if (isMainDrawer) {
      if (this.#menuState === 'closing') return this.#closePromise;
      if (this.#menuState === 'closed' && !details.hasAttribute('open')) return Promise.resolve();
      this.#menuState = 'closing';
      this.#swipeGesture = null;
      this.#scheduleScrollIndicatorUpdate();
    }

    const transitionVersion = isMainDrawer ? ++this.#transitionVersion : 0;

    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    const historyCleanupPromise = isMainDrawer && !options.skipHistoryCleanup
      ? this.#removeHistoryEntry()
      : Promise.resolve();

    const finishClose = () => {
      if (isMainDrawer && (transitionVersion !== this.#transitionVersion || this.#menuState !== 'closing')) return;

      if (isMainDrawer) {
        removeTrapFocus();
        reset(details);
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        syncScrollLock();
        this.#menuState = 'closed';
        this.#lastFocusedElement?.focus();
        this.#lastFocusedElement = null;
      } else {
        reset(details);
        trapFocus(this.refs.details);
      }
    };

    const animationPromise = isMainDrawer
      ? this.#animateMainDrawerClosed(details, drawer).then(finishClose)
      : onAnimationEnd(drawer || details, finishClose, { subtree: false });

    const closePromise = Promise.allSettled([animationPromise, historyCleanupPromise]);
    if (isMainDrawer) this.#closePromise = closePromise;
    return closePromise;
  }

  /**
   * Keep the main drawer rendered until its full-height closing transition completes.
   * @param {HTMLDetailsElement} details
   * @param {Element | null} drawer
   * @returns {Promise<void>}
   */
  async #animateMainDrawerClosed(details, drawer) {
    if (!(drawer instanceof HTMLElement)) return Promise.resolve();

    this.#swipeResetAnimation?.cancel();
    this.#swipeResetAnimation = null;

    const applyClosingState = () => {
      details.classList.add('menu-closing');
      details.classList.remove('menu-swiping', 'menu-swipe-resetting');
      details.style.removeProperty('--simedge-mobile-menu-swipe-y');
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      applyClosingState();
      return Promise.resolve();
    }

    const startTransform = drawer.style.transform || 'translate3d(0, 0, 0)';
    applyClosingState();
    drawer.style.willChange = 'transform';

    const animation = drawer.animate(
      [
        { transform: startTransform },
        { transform: 'translate3d(0, 100dvh, 0)' },
      ],
      {
        duration: MAIN_DRAWER_CLOSE_MS,
        easing: 'cubic-bezier(0.55, 0, 1, 0.45)',
        fill: 'forwards',
      }
    );
    this.#drawerAnimation = animation;

    const cancelledFallback = animation.finished.catch(
      () => new Promise((resolve) => setTimeout(resolve, MAIN_DRAWER_CLOSE_MS))
    );
    await Promise.race([
      cancelledFallback,
      new Promise((resolve) => setTimeout(resolve, MAIN_DRAWER_CLOSE_FALLBACK_MS)),
    ]);

    if (this.#drawerAnimation === animation) {
      animation.cancel();
      this.#drawerAnimation = null;
      drawer.style.removeProperty('transform');
      drawer.style.removeProperty('will-change');
    }
  }

  async #resetSwipePosition() {
    const details = this.refs.details;
    const drawer = this.refs.menuDrawer;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.classList.remove('menu-swiping', 'menu-swipe-resetting');
      details.style.removeProperty('--simedge-mobile-menu-swipe-y');
      drawer.style.removeProperty('transform');
      return;
    }

    const startTransform = drawer.style.transform || getComputedStyle(drawer).transform;
    details.classList.add('menu-swipe-resetting');
    details.classList.remove('menu-swiping');
    details.style.removeProperty('--simedge-mobile-menu-swipe-y');

    const animation = drawer.animate(
      [
        { transform: startTransform === 'none' ? 'translate3d(0, 0, 0)' : startTransform },
        { transform: 'translate3d(0, 0, 0)' },
      ],
      {
        duration: SWIPE_RESET_MS,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      }
    );
    this.#swipeResetAnimation = animation;

    await Promise.race([
      animation.finished.catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, SWIPE_RESET_MS + 80)),
    ]);

    if (this.#swipeResetAnimation === animation) {
      animation.cancel();
      this.#swipeResetAnimation = null;
      drawer.style.removeProperty('transform');
      details.classList.remove('menu-swipe-resetting');
    }
  }

  /**
   * Update tabs, panels and link tab stops without replacing the drawer.
   * @param {string | undefined} tabName
   * @param {boolean} [moveFocus]
   */
  #setActiveTab(tabName, moveFocus = false) {
    if (!tabName) return;

    const tabs = this.querySelectorAll('[role="tab"][data-menu-tab]');
    const panels = this.querySelectorAll('[role="tabpanel"][data-menu-panel]');
    if (!tabs.length || !panels.length) return;

    const tabList = this.querySelector('[role="tablist"]');
    if (tabList instanceof HTMLElement) tabList.dataset.activeTab = tabName;

    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const isActive = tab.dataset.menuTab === tabName;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive && moveFocus) tab.focus();
    });

    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.dataset.menuPanel === tabName;
      panel.hidden = !isActive;
      panel.querySelectorAll('a[href], button').forEach((control) => {
        if (control instanceof HTMLElement) control.tabIndex = isActive ? 0 : -1;
      });
      if (isActive) panel.scrollTop = 0;
    });

    this.#scheduleScrollIndicatorUpdate();
  }

  #pushHistoryEntry() {
    if (!this.refs.scrollIndicator || this.#historyEntryActive) return;

    const currentState = history.state && typeof history.state === 'object' ? history.state : {};
    history.pushState({ ...currentState, [MENU_HISTORY_KEY]: this.#historyToken }, '', window.location.href);
    this.#historyEntryActive = true;
  }

  #removeHistoryEntry() {
    if (!this.#historyEntryActive) return Promise.resolve();

    this.#historyEntryActive = false;
    if (history.state?.[MENU_HISTORY_KEY] !== this.#historyToken) return Promise.resolve();

    return new Promise((resolve) => {
      this.#historyCleanupResolve = resolve;
      history.back();
    });
  }

  #scheduleScrollIndicatorUpdate = () => {
    if (!this.refs.scrollIndicator || this.#scrollIndicatorFrame) return;

    this.#scrollIndicatorFrame = requestAnimationFrame(() => {
      this.#scrollIndicatorFrame = 0;
      const scrollIndicator = this.refs.scrollIndicator;
      if (!scrollIndicator) return;

      const activePanel = this.querySelector('[data-menu-panel]:not([hidden])');
      const showIndicator = this.isOpen && activePanel instanceof HTMLElement && hasMoreScrollContent(activePanel);
      scrollIndicator.toggleAttribute('hidden', !showIndicator);
    });
  };

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open', 'menu-closing', 'menu-swiping', 'menu-swipe-resetting');
  element.style.removeProperty('--simedge-mobile-menu-swipe-y');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * Keep the document scroll lock aligned with the actual open scroll-lock details elements.
 */
function syncScrollLock() {
  const hasOpenScrollLock = document.querySelector('details[scroll-lock][open]') !== null;
  document.documentElement.toggleAttribute('scroll-lock', hasOpenScrollLock);
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function hasMoreScrollContent(element) {
  return element.scrollHeight - element.clientHeight - element.scrollTop > 2;
}
